import React, { useEffect, useRef, useState } from 'react';
import { useAlert } from 'react-alert';
import { useHistory } from 'react-router-dom';
import api from '../../../seller-apis/api';
import { searchManufacturers } from '../../../seller-apis/apidataField';
import RegistrationStepper from '../RegistrationStepper';
import '../../../assets/css/newRegistration.css';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const GST_OPTIONS = [
  { value: '', label: 'Select GST' },
  { value: '0', label: '0%' },
  { value: '5', label: '5%' },
  { value: '18', label: '18%' },
];

const VALID_GST_VALUES = ['0', '5', '18'];
const MIN_PRODUCT_ROWS = 5;
const MAX_PRODUCT_ROWS = 50;
const MAX_PRODUCT_NAME_LENGTH = 60;

const createEmptyProduct = () => ({
  productName: '',
  price: '',
  gst: '',
  brand: '',
  manufacturer: '',
});

const createDefaultProducts = () =>
  Array.from({ length: MIN_PRODUCT_ROWS }, createEmptyProduct);

function validateProductRow(row) {
  const errors = {};
  const productName = (row.productName || '').trim();

  if (!productName) {
    errors.productName = 'Product name is required';
  } else if (productName.length < 2) {
    errors.productName = 'Product name must be at least 2 characters';
  } else if (productName.length > MAX_PRODUCT_NAME_LENGTH) {
    errors.productName = `Product name cannot exceed ${MAX_PRODUCT_NAME_LENGTH} characters`;
  }

  const priceRaw = row.price;
  if (priceRaw === '' || priceRaw === null || priceRaw === undefined) {
    errors.price = 'Price is required';
  } else {
    const price = Number(priceRaw);
    if (Number.isNaN(price) || !Number.isInteger(price) || price <= 0) {
      errors.price = 'Price must be a whole number greater than 0';
    }
  }

  if (!row.gst) {
    errors.gst = 'GST is required';
  } else if (!VALID_GST_VALUES.includes(String(row.gst))) {
    errors.gst = 'Please select 0%, 5%, or 18%';
  }

  const brand = (row.brand || '').trim();
  if (!brand) {
    errors.brand = 'Brand is required';
  } else if (brand.length < 2) {
    errors.brand = 'Brand must be at least 2 characters';
  }

  return errors;
}

function applyProductNameRules(products, errors) {
  const duplicateMap = new Map();

  products.forEach((row, index) => {
    const key = (row.productName || '').trim().toLowerCase();
    if (!key) return;
    if (!duplicateMap.has(key)) {
      duplicateMap.set(key, []);
    }
    duplicateMap.get(key).push(index);
  });

  products.forEach((row, index) => {
    const name = row.productName || '';
    const key = name.trim().toLowerCase();

    if (name.length > MAX_PRODUCT_NAME_LENGTH) {
      errors[index] = {
        ...errors[index],
        productName: `Max length is ${MAX_PRODUCT_NAME_LENGTH}`,
      };
      return;
    }

    if (key && (duplicateMap.get(key)?.length || 0) > 1) {
      errors[index] = {
        ...errors[index],
        productName: 'Product name must be unique',
      };
    }
  });

  return errors;
}

function applyProductNameOnlyErrors(products, errors = [], overflowIndex = -1) {
  const nextErrors = products.map((_, index) => {
    const rowErrors = { ...(errors[index] || {}) };
    delete rowErrors.productName;
    return rowErrors;
  });

  const duplicateMap = new Map();
  products.forEach((row, index) => {
    const key = (row.productName || '').trim().toLowerCase();
    if (!key) return;
    if (!duplicateMap.has(key)) {
      duplicateMap.set(key, []);
    }
    duplicateMap.get(key).push(index);
  });

  duplicateMap.forEach((indexes) => {
    if (indexes.length <= 1) return;
    indexes.forEach((index) => {
      nextErrors[index] = {
        ...nextErrors[index],
        productName: 'Product name must be unique',
      };
    });
  });

  if (overflowIndex >= 0 && nextErrors[overflowIndex]) {
    nextErrors[overflowIndex] = {
      ...nextErrors[overflowIndex],
      productName: `Max length is ${MAX_PRODUCT_NAME_LENGTH}`,
    };
  }

  return nextErrors;
}

function validateAllProducts(products) {
  const errors = products.map(validateProductRow);
  return applyProductNameRules(products, errors);
}

function isProductRowFilled(row) {
  return (
    (row.productName || '').trim() !== '' &&
    row.price !== '' &&
    row.price !== null &&
    row.price !== undefined &&
    row.gst !== '' &&
    (row.brand || '').trim() !== ''
  );
}

function normalizeBrandName(value) {
  return String(value || '').trim().toLowerCase();
}

function getManufacturerIdFromItem(item) {
  return item?.manufacturerId || item?._id || '';
}

function RegistrationStep2({
  initialProducts,
  completedStep = 1,
  activeStep = 2,
}) {
  const alert = useAlert();
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [products, setProducts] = useState(() => {
    if (!initialProducts?.length) return createDefaultProducts();
    const rows = initialProducts.slice(0, MAX_PRODUCT_ROWS);
    while (rows.length < MIN_PRODUCT_ROWS) {
      rows.push(createEmptyProduct());
    }
    return rows;
  });
  const [fieldErrors, setFieldErrors] = useState([]);
  const [brandSearchResults, setBrandSearchResults] = useState([]);
  const [activeBrandRowIndex, setActiveBrandRowIndex] = useState(null);
  const [isSearchingBrand, setIsSearchingBrand] = useState(false);
  const manufacturerByBrandNameRef = useRef({});

  useEffect(() => {
    if (!localStorage.getItem('lead')) {
      history.replace('/');
      return;
    }
  }, [history]);

  useEffect(() => {
    if (activeBrandRowIndex === null) return undefined;

    const typedBrand = (products[activeBrandRowIndex]?.brand || '').trim();
    if (!typedBrand) {
      setBrandSearchResults([]);
      setIsSearchingBrand(false);
      return undefined;
    }

    let cancelled = false;
    const timerId = setTimeout(() => {
      setIsSearchingBrand(true);
      api.brand
        .getManufacturers(searchManufacturers(typedBrand,{active:true}))
        .then((res) => {
          if (cancelled) return;
          const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
          list.forEach((item) => {
            const key = normalizeBrandName(item?.name);
            const manufacturerId = getManufacturerIdFromItem(item);
            if (key && manufacturerId) {
              manufacturerByBrandNameRef.current[key] = manufacturerId;
            }
          });
          setBrandSearchResults(
            list.filter((item) => item?.name && String(item.name).trim().length >= 1)
          );
        })
        .catch((err) => {
          if (cancelled) return;
          setBrandSearchResults([]);
          console.log(err);
        })
        .finally(() => {
          if (!cancelled) {
            setIsSearchingBrand(false);
          }
        });
    }, 1000);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [activeBrandRowIndex, products]);

  const updateProduct = (index, field, value) => {
    setProducts((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
    setFieldErrors((prev) => {
      if (!prev[index]?.[field]) return prev;
      const next = [...prev];
      const rowErr = { ...next[index] };
      delete rowErr[field];
      next[index] = rowErr;
      return next;
    });
  };

  const handleProductNameChange = (index, value) => {
    const overflowed = value.length > MAX_PRODUCT_NAME_LENGTH;
    const nextValue = value.slice(0, MAX_PRODUCT_NAME_LENGTH);
    setProducts((prev) => {
      const next = prev.map((row, i) =>
        i === index ? { ...row, productName: nextValue } : row
      );
      setFieldErrors((prevErrors) =>
        applyProductNameOnlyErrors(next, prevErrors, overflowed ? index : -1)
      );
      return next;
    });
  };

  const handleProductNameKeyDown = (index, event) => {
    const isTypingCharacter =
      event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
    if (!isTypingCharacter) return;
    if ((products[index]?.productName || '').length < MAX_PRODUCT_NAME_LENGTH) return;
    event.preventDefault();
    setFieldErrors((prevErrors) => applyProductNameOnlyErrors(products, prevErrors, index));
  };

  const handlePriceKeyDown = (event) => {
    if (event.key === '.' || event.key === ',' || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  };

  const handlePriceChange = (index, value) => {
    const nextValue = value === '' ? '' : value.replace(/[^\d]/g, '');
    updateProduct(index, 'price', nextValue);
  };

  const addProductRow = () => {
    if (products.length >= MAX_PRODUCT_ROWS) {
      return;
    }
    setProducts((prev) => {
      const next = [...prev, createEmptyProduct()];
      setFieldErrors((prevErrors) => applyProductNameOnlyErrors(next, prevErrors));
      return next;
    });
  };

  const removeProductRow = (index) => {
    setActiveBrandRowIndex((prev) => {
      if (prev === null) return prev;
      if (prev === index) return null;
      if (prev > index) return prev - 1;
      return prev;
    });
    setProducts((prev) => {
      if (prev.length <= MIN_PRODUCT_ROWS) return prev;
      const next = prev.filter((_, i) => i !== index);
      setFieldErrors((prevErrors) => {
        const filteredErrors = prevErrors.filter((_, i) => i !== index);
        return applyProductNameOnlyErrors(next, filteredErrors);
      });
      return next;
    });
  };

  const inputClassName = (index, field) => {
    const isSelect = field === 'gst';
    const base = isSelect ? 'reg-seller-input reg-seller-select' : 'reg-seller-input';
    const hasError = fieldErrors[index]?.[field];
    const isPlaceholder = isSelect && !products[index]?.[field];
    return `${base}${hasError ? ' reg-seller-input--error' : ''}${
      isPlaceholder ? ' reg-seller-select--placeholder' : ''
    }`;
  };

  const resolveManufacturerIds = (rows) => {
    const manufacturerMap = manufacturerByBrandNameRef.current;
    return rows.map((row) => {
      if (row.manufacturer) return row;
      const key = normalizeBrandName(row.brand);
      if (!key) return row;
      return { ...row, manufacturer: manufacturerMap[key] || '' };
    });
  };

  const handleReviewSubmit = async () => {
    const filledRowCount = products.filter(isProductRowFilled).length;
    if (filledRowCount < MIN_PRODUCT_ROWS) {
      return;
    }
    const productsWithManufacturer = resolveManufacturerIds(products);
    const errors = validateAllProducts(productsWithManufacturer);
    setFieldErrors(errors);

    const firstErrorRow = errors.findIndex((row) => Object.keys(row).length > 0);
    if (firstErrorRow !== -1) {
      const firstField = Object.keys(errors[firstErrorRow])[0];
      return;
    }

    const vendorLeadId = localStorage.getItem('lead');
    if (!vendorLeadId) {
      history.replace('/');
      return;
    }

    const validatedProducts = productsWithManufacturer.map((row) => ({
      ...row,
      productName: row.productName.trim(),
      brand: row.brand.trim(),
      manufacturer: row.manufacturer,
      price: Number(row.price),
      gst: Number(row.gst),
    }));

    const payload = {
      vendor_lead: vendorLeadId,
      data: validatedProducts.map((row) => ({
        vendor_lead: vendorLeadId,
        product_name: row.productName,
        price: row.price,
        gst: row.gst,
        brand: row.brand,
        manufacturer: row.manufacturer,
      })),
    };

    setIsSubmitting(true);
    try {
      const res = await api.vendorLead.vendorLeadProducts(payload);
      history.push(`/register-success/${vendorLeadId}`);
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Failed to submit products';
      alert.error(message);
      console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFieldError = (index, field) =>
    fieldErrors[index]?.[field] ? (
      <span className="reg-seller-field-error" role="alert">
        {fieldErrors[index][field]}
      </span>
    ) : null;

  const filledRowCount = products.filter(isProductRowFilled).length;
  const canSubmit = filledRowCount >= MIN_PRODUCT_ROWS && !isSubmitting;

  const getManufacturerId = (item) => getManufacturerIdFromItem(item)

  const handleBrandChange = (index, value) => {
    setActiveBrandRowIndex(index);
    setProducts((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, brand: value, manufacturer: '' } : row
      )
    );
    setFieldErrors((prev) => {
      if (!prev[index]?.brand) return prev;
      const next = [...prev];
      const rowErr = { ...next[index] };
      delete rowErr.brand;
      next[index] = rowErr;
      return next;
    });
  };

  const handleBrandSelect = (index, item) => {
    const selectedName = item?.name || '';
    const selectedManufacturerId = getManufacturerId(item);
    const key = normalizeBrandName(selectedName);
    if (key && selectedManufacturerId) {
      manufacturerByBrandNameRef.current[key] = selectedManufacturerId;
    }
    setProducts((prev) =>
      prev.map((row, i) =>
        i === index
          ? { ...row, brand: selectedName, manufacturer: selectedManufacturerId }
          : row
      )
    );
    
    setFieldErrors((prev) => {
      if (!prev[index]?.brand) return prev;
      const next = [...prev];
      const rowErr = { ...next[index] };
      delete rowErr.brand;
      next[index] = rowErr;
      return next;
    });
    setActiveBrandRowIndex(null);
    setBrandSearchResults([]);
  };

  return (
    <div className="reg-seller-page">
    <div className="reg-seller-flow">
      <header className="reg-seller-flow__header">
        <h1 className="reg-seller-flow__title">Join as a Seller</h1>
        <p className="reg-seller-flow__subtitle">Step 2 of 2 — Add Key Products</p>
      </header>

      <RegistrationStepper completedStep={completedStep} activeStep={activeStep} />

      <section className="reg-seller-card">
        <div className="reg-seller-card__header">
          <h2 className="reg-seller-card__title">Add Key Products</h2>
          <p className="reg-seller-card__hint">
            Help us understand your product range by adding your key products below. (Min. 5 required)
          </p>
        </div>

        <div className="reg-seller-product-grid">
          <div className="reg-seller-product-grid__row reg-seller-product-grid__row--head">
            <div className="reg-seller-product-grid__cell reg-seller-product-grid__cell--num">
              #
            </div>
            <div className="reg-seller-product-grid__cell">
              Product Name<span className="reg-seller-required">*</span>
            </div>
            <div className="reg-seller-product-grid__cell">
              Price (₹)<span className="reg-seller-required">*</span> <OverlayTrigger
                  key="register-key-prd-price"
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-register-key-prd-price`} className="fs-10 reg-key-product-price-tooltip">
                      <p className='fs-12 reg-key-product-price-tt-inner'>This would be your transfer price to Toolsvilla. No commission would be deducted on this price.</p>
                    </Tooltip>
                  }
                >
                  <i class="bi bi-info-circle ms-1"></i>
                </OverlayTrigger>
            </div>
            <div className="reg-seller-product-grid__cell">
              GST (%)<span className="reg-seller-required">*</span>
            </div>
            <div className="reg-seller-product-grid__cell">
              Brand<span className="reg-seller-required">*</span>
            </div>
            <div className="reg-seller-product-grid__cell reg-seller-product-grid__cell--action">
              Action
            </div>
          </div>

          {products.map((row, index) => (
            <div className="reg-seller-product-grid__row" key={index}>
              <div className="reg-seller-product-grid__cell reg-seller-product-grid__cell--num">
                {index + 1}
              </div>
              <div className="reg-seller-product-grid__cell reg-seller-product-grid__cell--field">
                <input
                  type="text"
                  className={inputClassName(index, 'productName')}
                  placeholder="Enter product name"
                  value={row.productName}
                  minLength={2}
                  maxLength={MAX_PRODUCT_NAME_LENGTH}
                  onKeyDown={(e) => handleProductNameKeyDown(index, e)}
                  onChange={(e) => handleProductNameChange(index, e.target.value)}
                />
                {renderFieldError(index, 'productName')}
              </div>
              <div className="reg-seller-product-grid__cell reg-seller-product-grid__cell--field">
                <input
                  type="number"
                  min="0"
                  step="1"
                  className={inputClassName(index, 'price')}
                  placeholder="Enter price"
                  value={row.price}
                  onKeyDown={handlePriceKeyDown}
                  onChange={(e) => handlePriceChange(index, e.target.value)}
                  onWheel={(e) => e.target.blur()}
                />
                {renderFieldError(index, 'price')}
              </div>
              <div className="reg-seller-product-grid__cell reg-seller-product-grid__cell--field">
                <select
                  className={inputClassName(index, 'gst')}
                  value={row.gst}
                  onChange={(e) => updateProduct(index, 'gst', e.target.value)}
                >
                  {GST_OPTIONS.map((opt) => (
                    <option key={opt.value || 'placeholder'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {renderFieldError(index, 'gst')}
              </div>
              <div
                className={`reg-seller-product-grid__cell reg-seller-product-grid__cell--field reg-seller-product-grid__cell__brand${
                  activeBrandRowIndex === index ? ' reg-seller-product-grid__cell__brand--active' : ''
                }`}
              >
                <div className="reg-seller-brand-input-wrap">
                  <input
                    type="text"
                    className={inputClassName(index, 'brand')}
                    placeholder="Search brand"
                    value={row.brand}
                    minLength={2}
                    onFocus={() => setActiveBrandRowIndex(index)}
                    onBlur={() => {
                      window.setTimeout(() => {
                        setActiveBrandRowIndex((current) => (current === index ? null : current));
                      }, 150);
                    }}
                    onChange={(e) => handleBrandChange(index, e.target.value)}
                  />
                  {activeBrandRowIndex === index && (isSearchingBrand || row.brand.trim()) ? (
                    <div className="reg-seller-brand-search">
                      {isSearchingBrand ? (
                        <div className="reg-seller-brand-search__empty">Searching...</div>
                      ) : brandSearchResults.length > 0 ? (
                        brandSearchResults.map((item, itemIndex) => (
                          <button
                            type="button"
                            key={getManufacturerId(item) || `${item?.name || 'brand'}-${itemIndex}`}
                            className="reg-seller-brand-search__item"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleBrandSelect(index, item)}
                          >
                            {item?.name}
                          </button>
                        ))
                      ) : (
                        <div className="reg-seller-brand-search__empty">No brands found</div>
                      )}
                    </div>
                  ) : null}
                </div>
                {renderFieldError(index, 'brand')}
              </div>
              <div className="reg-seller-product-grid__cell reg-seller-product-grid__cell--action">
                <button
                  type="button"
                  className="reg-seller-remove-btn"
                  onClick={() => removeProductRow(index)}
                  disabled={products.length <= MIN_PRODUCT_ROWS}
                  aria-label={`Remove product ${index + 1}`}
                >
                  <i className="bi bi-trash" aria-hidden="true" />
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="reg-seller-add-row-btn"
          onClick={addProductRow}
          disabled={products.length >= MAX_PRODUCT_ROWS}
        >
         <i className="bi bi-plus-circle"></i>
          Add Another Product
        </button>
      </section>

      <footer className="reg-seller-flow__footer">
        <button
          type="button"
          className="reg-seller-submit-btn"
          onClick={handleReviewSubmit}
          disabled={!canSubmit}
        >
          {isSubmitting ? 'Submitting...' : 'Review & Submit'}
          <i className="bi bi-arrow-right" aria-hidden="true" />
        </button>
      </footer>
    </div>
    </div>
  );
}

export default RegistrationStep2;
